import React from 'react';

import { A11YLiveMessage } from '@lumapps/aria-live/components/A11YLiveMessage';
import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Form } from '@lumapps/lumx-forms/components/Form';
import { FormTextField } from '@lumapps/lumx-forms/components/FormTextField';
import { mdiClose } from '@lumapps/lumx/icons';
import { Alignment, Emphasis, FlexBox, IconButton, Message, Orientation, Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { ASK_AI } from '../../keys';
import { AskAiDetailedFeedback, AskAiFeedbackTypes } from '../../types';
import { AskAiFeedbackReasonOptions } from './AskAiFeedbackChips';
import './index.scss';

const CLASSNAME = 'feedback-reasons';

export interface AskAiFeedbackReasonsProps {
    isOpen: boolean;
    onClose: () => void;
    sendFeedback: (feedback: AskAiDetailedFeedback) => void;
}

export const AskAiFeedbackReasons = ({ isOpen, onClose, sendFeedback }: AskAiFeedbackReasonsProps) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(CLASSNAME);

    const [isFeedbackSent, setIsFeedbackSent] = React.useState(false);

    const sendDetailedFeedback = (feedback?: AskAiDetailedFeedback) => {
        setIsFeedbackSent(true);
        onClose();

        if (feedback) {
            sendFeedback(feedback);
        }
    };

    return (
        <A11YLiveMessage className={block()}>
            {isOpen && (
                <FlexBox className={element('choices')} orientation={Orientation.vertical} gap={Size.big}>
                    <Form
                        form={{
                            values: {
                                type: AskAiFeedbackTypes.DISLIKED,
                                reason: '',
                            },
                        }}
                        onSubmit={sendDetailedFeedback}
                        formActionProps={{
                            saveProps: {
                                emphasis: Emphasis.medium,
                                children: translateKey(GLOBAL.SEND),
                            },
                        }}
                    >
                        <FlexBox
                            orientation={Orientation.horizontal}
                            hAlign={Alignment.top}
                            vAlign={Alignment.spaceBetween}
                        >
                            <AskAiFeedbackReasonOptions />
                            <IconButton
                                emphasis="low"
                                size="m"
                                icon={mdiClose}
                                label={translateKey(GLOBAL.CLOSE)}
                                onClick={onClose}
                                {...get({ element: 'button', action: 'close' })}
                            />
                        </FlexBox>

                        <FormTextField
                            name="comment"
                            label={translateKey(ASK_AI.HOW_CAN_ANSWER_BE_IMPROVED)}
                            placeholder={translateKey(GLOBAL.ADD_A_COMMENT)}
                            multiline
                            minimumRows={1}
                            maxLength={500}
                        />
                    </Form>
                </FlexBox>
            )}

            {!isOpen && isFeedbackSent && (
                <Message kind="success" hasBackground>
                    {translateKey(ASK_AI.FEEDBACK_SENT)}
                </Message>
            )}
        </A11YLiveMessage>
    );
};
